import { HistoryUpdatesItemType } from 'src/components/History/types';
import { ReceivingType } from '../../types/receiving';

export type EditKeysType = keyof ReceivingType;

export const PROPERTY_EDIT_KEYS = ['FILES'] as const;

export type PropertyEditKeysType = (typeof PROPERTY_EDIT_KEYS)[number];

export const EDIT_KEYS = [
    'ESHOP_RECEIVING_ID',
    'STATUS',
    'URGENT',
    'DESCRIPTION',
    'PRODUCTS',
    'PACKAGINGS',
    'SUPPLIER',
    'DELIVERY_TYPE',
    'DELIVERY_NOTE_NUMBER',
    'ASN_LABEL_EMAIL',
    'UNITS_AMOUNT',
    'DUTY',
    'DRIVER_COMPANY',
    'DRIVER_COMPANY_OTHER_NAME',
    'DRIVER_NAME',
    'ESTIMATED_DELIVERY',
    'DRIVER_PHONE',
    'DRIVER_DISPATCHER_CONTACT',
    'DRIVER_TRAILER_LICENSE_PLATE',
    'TRACKING_NUMBER',
    'ESHOP_NOTE',
    'PRIORITY',
    'CONTAINER_NUMBER',
    'ANNOUNCED_PALLETS_COUNT',
    'RECEIVING_TYPE',
    'INCOTERMS',
    'FILES',
];

export const historyUpdates: HistoryUpdatesItemType[] = [
    { key: 'CREATED_AT', translateKey: 'receivingDetail.createdAt', format: 'date' },
    { key: 'STATUS', translateKey: 'receivingDetail.status' },
    { key: 'ASN', translateKey: 'receivingDetail.asn' },
    { key: 'ESHOP_RECEIVING_ID', translateKey: 'receivingDetail.eshopReceivingId' },
    { key: 'URGENT', translateKey: 'receivingDetail.urgent' },
    { key: 'DESCRIPTION', translateKey: 'receivingDetail.description' },
    { key: 'SUPPLIER', translateKey: 'receivingDetail.supplier' },
    { key: 'DELIVERY_TYPE', translateKey: 'receivingDetail.deliveryType' },
    { key: 'DELIVERY_NOTE_NUMBER', translateKey: 'receivingDetail.deliveryNoteNumber' },
    { key: 'ASN_LABEL_EMAIL', translateKey: 'receivingDetail.asnLabelEmail' },
    { key: 'UNITS_AMOUNT', translateKey: 'receivingDetail.amountOfHandlingUnits', format: 'number' },
    { key: 'DUTY', translateKey: 'receivingDetail.duty' },
    { key: 'DRIVER_COMPANY', translateKey: 'receivingDetail.driverCompany' },
    { key: 'DRIVER_COMPANY_OTHER_NAME', translateKey: 'receivingDetail.driverCompanyOtherName' },
    { key: 'DRIVER_NAME', translateKey: 'receivingDetail.driverName' },
    { key: 'ESTIMATED_DELIVERY', translateKey: 'receivingDetail.estimatedDelivery', format: 'date' },
    { key: 'DRIVER_PHONE', translateKey: 'receivingDetail.driverPhone' },
    { key: 'DRIVER_DISPATCHER_CONTACT', translateKey: 'receivingDetail.dispatcherContact' },
    { key: 'DRIVER_TRAILER_LICENSE_PLATE', translateKey: 'receivingDetail.driverTrailerLicensePlate' },
    { key: 'TRACKING_NUMBER', translateKey: 'receivingDetail.trackingNumber' },
    { key: 'ESHOP_NOTE', translateKey: 'receivingDetail.eshopNote' },
    { key: 'PRIORITY', translateKey: 'receivingDetail.priority' },
    { key: 'CONTAINER_NUMBER', translateKey: 'receivingDetail.containerNumber' },
    { key: 'ANNOUNCED_PALLETS_COUNT', translateKey: 'receivingDetail.announcedPalletsCount', format: 'number' },
    { key: 'RECEIVING_TYPE', translateKey: 'receivingDetail.receivingType' },
    { key: 'INCOTERMS', translateKey: 'receivingDetail.incoterms' },
    { key: 'UNLOADED_AT', translateKey: 'receivingDetail.unloadedAt', format: 'date' },
    { key: 'RECEIVED_AT', translateKey: 'receivingDetail.receivedAt', format: 'date' },
    { key: 'CONFIRMED_AT', translateKey: 'receivingDetail.confirmedAt', format: 'date' },
    { key: 'DELIVERED_PALLETS_COUNT', translateKey: 'receivingDetail.deliveredPalletsCount', format: 'number' },
    { key: 'DELIVERED_STANDARD_PALLETS_COUNT', translateKey: 'receivingDetail.deliveredStandardPalletsCount', format: 'number' },
    { key: 'DELIVERED_OTHER_PALLETS_COUNT', translateKey: 'receivingDetail.deliveredOtherPalletsCount', format: 'number' },
    { key: 'DELIVERED_BROKEN_PALLETS_COUNT', translateKey: 'receivingDetail.deliveredBrokenPalletsCount', format: 'number' },
    { key: 'PHOTO_DOCUMENTATION_TAKEN', translateKey: 'receivingDetail.photoDocumentationTaken' },
    { key: 'REAL_DELIVERY', translateKey: 'receivingDetail.realDelivery', format: 'date' },
    { key: 'RETURNED_EUR_PALLETS_COUNT', translateKey: 'receivingDetail.returnedEurPalletsCount', format: 'number' },
    { key: 'RETURNED_STANDARD_PALLETS_COUNT', translateKey: 'receivingDetail.returnedStandardPalletsCount', format: 'number' },
    { key: 'SKLADON_NOTE', translateKey: 'receivingDetail.skladonNote' },
    { key: 'NOTE', translateKey: 'receivingDetail.note' },
    { key: 'CANCELED_AT', translateKey: 'receivingDetail.canceledAt', format: 'date' },
    { key: 'SLA_DATE', translateKey: 'receivingDetail.slaDate', format: 'date' },
    { key: 'DELIVERED_CARTONS_COUNT', translateKey: 'receivingDetail.deliveredCartonsCount', format: 'number' },
    { key: 'SLA_SUSPENDED', translateKey: 'receivingDetail.slaSuspended' },
];

export const historyUpdatesSkladonUser: HistoryUpdatesItemType[] = [
    ...historyUpdates,
    { key: 'ID', translateKey: 'products.table.id', format: 'number' },
    { key: 'CREATOR_ID', translateKey: 'users.table.creatorId', format: 'number' },
    { key: 'ESHOP_KEY', translateKey: 'providerDetail.eshopKey' },
    { key: 'ORDER_ID', translateKey: 'returnDetail.orderId', format: 'number' },
    { key: 'DCIX_EXPORT', translateKey: 'receivingDetail.dcixExport', format: 'date' },
];

/** Keys never shown for CREATE history entries (still shown on EDIT). */
export const historyCreateHiddenKeys: string[] = ['ESHOP_KEY'];

export const historyProductUpdates: HistoryUpdatesItemType[] = [
    { key: 'PRODUCT.NAME', translateKey: 'receivingDetail.productTable.name' },
    { key: 'PRODUCT.SKU', translateKey: 'receivingDetail.productTable.sku', keyRequiredExclude: 'SKU' },
    { key: 'QUANTITY', translateKey: 'receivingDetail.productTable.quantity', format: 'number' },
    { key: 'RECEIVED', translateKey: 'receivingDetail.productTable.receivedQuantity', format: 'number' },
    { key: 'DIFF_QUANTITY', translateKey: 'receivingDetail.productTable.differential', format: 'number' },
    { key: 'LOT', translateKey: 'receivingDetail.productTable.lot' },
    { key: 'EXPIRATION_DATE', translateKey: 'receivingDetail.productTable.expirationDate', format: 'date' },
    { key: 'EAN', translateKey: 'receivingDetail.productTable.ean' },
    { key: 'DESCRIPTION', translateKey: 'receivingDetail.productTable.description' },
    { key: 'PRODUCT_TYPE', translateKey: 'receivingDetail.productTable.productType' },
    { key: 'LINE_STATE', translateKey: 'receivingDetail.productTable.lineState' },
    { key: 'PRODUCT_PRICE', translateKey: 'orders.table.productPrice', format: 'number' },
];
export const historyPackagingUpdates: HistoryUpdatesItemType[] = [
    { key: 'PACKAGING_TYPE', translateKey: 'receivingDetail.packagingTable.packagingType' },
    { key: 'PALLET', translateKey: 'receivingDetail.packagingTable.pallet' },
    { key: 'QUANTITY', translateKey: 'receivingDetail.packagingTable.quantity', format: 'number' },
];

export const historyReceivingFilesUpdates: HistoryUpdatesItemType[] = [
    { key: 'ORIGINAL_FILE_NAME', translateKey: 'receivingDetail.attachmentsHistoryFileName' },
    { key: 'DESCRIPTION', translateKey: 'file.description' },
];

export const statusesTo: { [key: string]: string[] } = {
    awaiting: ['canceled', 'received'],
};

export const STORAGE_KEY = 'receivingEditData';
